/* The Record Keeping WALK VIEW — a view, never a queue.
 *
 * The walk WORK is not here: a look-through node the rules could not
 * close is the deck's fourth stage (#1065), so it is a page of the
 * pager above, counted by the line as "to walk". This view carries only
 * what the walk SAYS about a node that has no card of its own.
 *
 * ``chain_orphan`` and ``look_through_incomplete`` are NOT ROWS
 * ANYWHERE: "no chain head yet" / "no target yet". They carry no
 * officer verb (both auto-resolve through the chain_check listener), so
 * putting them in a queue would hand the officer a button that cannot
 * do anything. The placement oracle asserts STATUS placement for
 * exactly these two — which is what stops them from being quietly
 * dropped instead, and it agrees with the deck assembler's own
 * WALK_STATUS_TASK_TYPES.
 */

import { useEffect, useState } from 'react'
import { apiFetch } from '../../lib/api'
import type { TaskQueueItem } from '../../lib/tasks'
import { fetchWalkStatus, type WalkNode } from '../fund/fundArea'
import { placementFor, PLACEMENT, modeOf } from './placement'

export default function WalkView({ tasks }: { tasks: TaskQueueItem[] }) {
  const statusTypes = Object.keys(PLACEMENT).filter((t) => modeOf(t) === 'status')
  const counts = new Map<string, number>(statusTypes.map((t) => [t, 0]))
  for (const t of tasks)
    if (counts.has(t.task_type)) counts.set(t.task_type, (counts.get(t.task_type) ?? 0) + 1)

  /* THE WALK LOG (locked 2026-08-31, mock-lookthrough §2): bar-clearing
   * leaves never become cards — one audit line each, the rule in plain
   * words, right column "automatic". Never a Bible citation. */
  const [log, setLog] = useState<WalkNode[]>([])
  useEffect(() => {
    let dead = false
    ;(async () => {
      try {
        const funds = await apiFetch<{ id: string }[]>('/api/funds')
        const lists = await Promise.all(
          funds.slice(0, 10).map((f) => fetchWalkStatus(f.id).catch(() => [] as WalkNode[]))
        )
        if (!dead) setLog(lists.flat().filter((n) => n.state === 'by_rule'))
      } catch {
        if (!dead) setLog([])
      }
    })()
    return () => {
      dead = true
    }
  }, [])

  return (
    <section className="walk" data-testid="wb-walk-view">
      <h2>Walk status</h2>
      <p className="sub">
        The nodes to walk are cards in the deck above. These are states of the chain, not work —
        they clear when the chain they describe is completed.
      </p>
      {statusTypes.map((t) => (
        <div className="wstatus" data-testid="wb-walk-status" data-task-type={t} key={t}>
          <b>{counts.get(t) ?? 0}</b>
          <span>{placementFor(t).statusLine}</span>
        </div>
      ))}
      {log.length ? (
        <>
          <h2 style={{ marginTop: 14 }}>Walk log</h2>
          <p className="sub">Chain ends the rules closed — an audit line each, never a card.</p>
          {log.map((n) => (
            <div
              className="wstatus"
              data-testid="wb-walk-log-line"
              key={n.entity_id}
              style={{ display: 'flex', gap: 10, alignItems: 'baseline' }}
            >
              <span className="mono" style={{ fontSize: 'var(--text-12)', color: 'var(--faint)' }}>
                by rule
              </span>
              <span>
                <b>{n.entity_name}</b> — {n.rule_label}
              </span>
              <span
                className="mono"
                style={{ marginLeft: 'auto', fontSize: 'var(--text-12)', color: 'var(--faint)' }}
              >
                automatic
              </span>
            </div>
          ))}
        </>
      ) : null}
    </section>
  )
}
